/**
 * 연식 비교 — 연식 코호트 비교 (DESIGN §8.10 · v0.5 에서 「노후화」를 개명).
 *
 * **이름을 바꾼 이유.** 이 화면은 지금 시점의 코호트를 나란히 놓을 뿐 노후를 판정하지
 * 않는다. 판정은 「노후화 분석」(§8.11)이 맡는다 — 이름이 판정을 약속하면 화면이 그
 * 약속을 지키지 못한다.
 *
 * **묻는 것은 "오래된 설비가 더 고장나는가" 하나다.** 그래서 고장 분석에 속한다.
 * 이용률을 함께 보는 것은 사용량 이야기를 하려는 것이 아니라 **교란을 확인하기
 * 위해서**다 — 이용이 없으면 이상도 관측되지 않으므로, 안 쓰이는 노후기의 높은
 * 가동률은 「멀쩡하다」가 아닐 수 있다.
 *
 * **하지 않는 것을 먼저 정한다:** 연식 회귀선·수명 예측·잔존가치 추정. 관측
 * 창이 수십 일이라 횡단면 비교밖에 할 수 없고, 그 위에 추세선을 얹으면 없는
 * 시계열을 그린 것이 된다.
 */

import { Section } from '@/components/Section'
import { useObservationDays } from '@/lib/window'
import { AgingCohort } from './aging/AgingCohort'
import { AgingSize } from './aging/AgingSize'
import './AgingPage.css'

export function AgingPage() {
  const days = useObservationDays()
  return (
    <div className="aging">
      <header className="aging__intro">
        <p className="aging__lead">
          이 화면은 <strong>같은 시점에 서 있는 연식 코호트를 견줍니다.</strong> 시간에 따른
          열화를 그린 것이 아닙니다 — 관측 창이 {days != null ? <span className="num">{days}일</span> : '짧아'}
          {days != null ? '이라 ' : ' '}잴 수 있는 것은 「지금 시점의 코호트 간 차이」뿐입니다. 노후 여부를 통제된 비교로
          묻는 것은 <a href="#/deterioration">노후화 분석</a> 화면입니다.
        </p>
        <p className="aging__warn">
          <strong>실측이 먼저 말하는 것:</strong> 가장 나쁜 코호트가 가장 오래된 코호트가
          아닙니다. 즉 이 데이터에서 <strong>연식은 노후도의 대리변수로 그냥 쓸 수 없습니다</strong> —
          연식별로 사업자·제조사·설치 환경 구성이 다르기 때문입니다. 왼쪽 필터의{' '}
          <strong>설치 연식</strong>으로 특정 연식이나 기간을 골라 좁혀 볼 수 있습니다.
        </p>
        <p className="aging__aside">
          <strong>별도 관리:</strong> 한 사업자가 그 연식을 거의 다 차지한 경우는 아래 두 그래프에서
          뺐습니다. 예를 들어 <strong>2006년 166기는 전부 한 사업자</strong>의 것이고, 같은 개소에
          2026년산과 같은 기종으로 섞여 있습니다 — 설비의 나이가 아니라 그 사업자의 입력 관행입니다.
          <strong>지운 것이 아닙니다</strong>: 표 보기 아래쪽에 값과 사유가 그대로 있고, 그 충전기들은
          실제로 동작합니다.
        </p>
      </header>

      <Section
        index={1}
        title="먼저, 연식에 따라 가동률이 달라지는가"
        lead="달라진다면 단조로운가. 이용률을 겹쳐 놓아야 '멀쩡한 것'과 '안 쓰여서 좋아 보이는 것'이 갈린다."
        layout="full"
      >
        <AgingCohort />
      </Section>

      <Section
        index={2}
        title="그리고, 그 코호트는 얼마나 남아 있는가"
        lead="철거된 노후기는 모집단에 없다. 오래된 코호트의 대수는 '그 해 보급량'이 아니라 '지금까지 남은 수'다 — 생존 편향의 크기를 여기서 본다."
        layout="full"
      >
        <AgingSize />
      </Section>
    </div>
  )
}
